<div class="flex h-full w-full flex-col">
  <form
    class="flex w-full flex-1 flex-col gap-4"
    (ngSubmit)="onSubmit()"
    [formGroup]="form">
    <!-- Name Field -->
    <div class="flex w-full flex-col gap-2">
      <p class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
        {{ nameKey | translate }}
      </p>
      <input
        formControlName="name"
        class="border-default bg-elevated focus:ring-accent-focus w-full rounded-lg border px-3 py-2.5 text-sm transition-all focus:border-transparent focus:ring-2 focus:outline-none"
        [class.border-red-500]="form.get('name')?.invalid && form.get('name')?.touched"
        [class.focus:ring-red-500]="form.get('name')?.invalid && form.get('name')?.touched"
        type="text" />

      @if (form.get('name')?.invalid && form.get('name')?.touched) {
        <div class="flex flex-col gap-1 text-xs text-red-500">
          @if (form.get('name')?.errors?.['required']) {
            <span>{{ 'form.error.required' | translate }}</span>
          }
          @if (form.get('name')?.errors?.['minlength']) {
            <span>
              {{
                'form.error.minlength' | translate: form.get('name')?.errors?.['minlength']
              }}
            </span>
          }
          @if (form.get('name')?.errors?.['maxlength']) {
            <span>
              {{
                'form.error.maxlength' | translate: form.get('name')?.errors?.['maxlength']
              }}
            </span>
          }
        </div>
      }
    </div>

    <!-- Recipe Dropdown -->
    <div class="flex w-full flex-col gap-2">
      <p class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
        {{ recipeDropdownLabelKey | translate }}
      </p>
      <div class="group relative flex rounded-md">
        <button
          #recipeDropdownTrigger
          (click)="toggleRecipeDropdown($event)"
          [attr.aria-expanded]="isRecipeDropdownOpen()"
          aria-haspopup="true"
          class="border-default bg-elevated hover:bg-interactive focus:ring-accent-focus flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all hover:shadow-sm focus:border-transparent focus:ring-2 focus:outline-none"
          type="button">
          <div class="flex items-center gap-2">
            <i [class]="recipeDropdownIconClass" class="text-base"></i>
            <span>{{ getSelectedRecipeLabel() }}</span>
          </div>
          <svg
            [class.rotate-180]="isRecipeDropdownOpen()"
            aria-hidden="true"
            class="duration-base size-5 transition-transform"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              clip-rule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              fill-rule="evenodd" />
          </svg>
        </button>

        <app-dropdown
          [target]="recipeDropdownTrigger"
          [template]="recipeDropdownContent"
          [isOpen]="isRecipeDropdownOpen()"
          [onClose]="onCloseRecipeDropdown"
          [closeOnClickOutside]="true"
          [closeOnEscape]="true" />

        <app-tooltip [target]="recipeDropdownTrigger" [content]="recipeDropdownTooltip()" />
      </div>
    </div>

    <!-- Recipe parameters: rendered only when the recipe declares any -->
    @if (schemaLoading()) {
      <p class="text-text-tertiary text-xs">
        {{ 'recipe_parameters.loading' | translate }}
      </p>
    } @else if (schemaFailed()) {
      <div
        class="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
        {{ 'recipe_parameters.schema_failed' | translate }}
      </div>
    } @else if (submissionError()?.recipeMisconfigured) {
      <div
        class="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
        {{ 'recipe_parameters.recipe_misconfigured' | translate }}
      </div>
    } @else if (showParameterForm()) {
      <div class="border-default flex w-full flex-col gap-2 rounded-lg border p-3">
        <p class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
          {{ 'recipe_parameters.title' | translate }}
        </p>
        <app-recipe-parameter-form
          [schema]="parameterSchema()"
          [values]="parameterValues()"
          (valuesChange)="onParameterValuesChange($event)"
          [serverErrors]="submissionError()?.fields ?? {}"
          [showErrors]="submitAttempted()" />
        @if (submissionError()?.formMessage; as message) {
          <p class="text-xs text-red-600 dark:text-red-400">{{ message }}</p>
        }
      </div>
    }

    <!-- Solver Dropdown -->
    <div class="flex w-full flex-col gap-2">
      <p class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
        {{ solverKey | translate }}
      </p>
      <div class="group relative flex rounded-md">
        <button
          #solverDropdownTrigger
          (click)="toggleSolverDropdown($event)"
          [attr.aria-expanded]="isSolverDropdownOpen()"
          aria-haspopup="true"
          class="border-default bg-elevated hover:bg-interactive focus:ring-accent-focus flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all hover:shadow-sm focus:border-transparent focus:ring-2 focus:outline-none"
          type="button">
          <div class="flex items-center gap-2">
            <i [class]="solverDropdownIconClass" class="text-base"></i>
            <span>{{ getSelectedSolverLabel() }}</span>
          </div>
          <svg
            [class.rotate-180]="isSolverDropdownOpen()"
            aria-hidden="true"
            class="duration-base size-5 transition-transform"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              clip-rule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              fill-rule="evenodd" />
          </svg>
        </button>

        <app-dropdown
          [target]="solverDropdownTrigger"
          [template]="solverDropdownContent"
          [isOpen]="isSolverDropdownOpen()"
          [onClose]="onCloseSolverDropdown"
          [closeOnClickOutside]="true"
          [closeOnEscape]="true" />

        <app-tooltip [target]="solverDropdownTrigger" [content]="solverDropdownTooltip()" />
      </div>
    </div>

    <!-- Description Field -->
    <div class="flex w-full flex-col gap-2">
      <p class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
        {{ descriptionKey | translate }}
      </p>
      <textarea
        formControlName="description"
        class="border-default bg-elevated focus:ring-accent-focus min-h-[80px] w-full resize-y rounded-lg border px-3 py-2.5 text-sm transition-all focus:border-transparent focus:ring-2 focus:outline-none"
        [class.border-red-500]="
          form.get('description')?.invalid && form.get('description')?.touched
        "
        [class.focus:ring-red-500]="
          form.get('description')?.invalid && form.get('description')?.touched
        ">
      </textarea>

      @if (form.get('description')?.invalid && form.get('description')?.touched) {
        <div class="flex flex-col gap-1 text-xs text-red-500">
          @if (form.get('description')?.errors?.['maxlength']) {
            <span>
              {{
                'form.error.maxlength'
                  | translate: form.get('description')?.errors?.['maxlength']
              }}
            </span>
          }
        </div>
      }
    </div>

    <div class="flex w-full flex-col gap-2">
      <label
        for="new-planning-space-tags"
        class="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
        {{ 'common.tags' | translate }}
      </label>
      <app-tags-input [id]="'new-planning-space-tags'" formControlName="tags" />
    </div>

    <!-- Internal Plan Field -->
    @if (canCreateInternal()) {
      <div class="flex w-full flex-col gap-2">
        <app-checkbox
          align="start"
          [checked]="form.controls.isInternal.value ?? false"
          [onChange]="setIsInternal">
          <span class="text-text-primary ml-2 text-[12.5px] font-medium">
            {{ 'features.planning_space_list.new_from_recipe.internal_label' | translate }}
            <span class="text-text-tertiary mt-0.5 block text-[11.5px] font-normal">
              {{ 'features.planning_space_list.new_from_recipe.internal_hint' | translate }}
            </span>
          </span>
        </app-checkbox>
      </div>
    }
  </form>
</div>

<ng-template #recipeDropdownContent>
  <div class="min-w-48">
    <div class="max-h-96 overflow-y-auto">
      @for (item of availableRecipes(); track $index) {
        <div
          class="hover:bg-interactive flex w-full cursor-pointer flex-row items-center justify-between rounded-lg px-1 py-0.5">
          <div
            class="relative mr-0.5 ml-2 flex items-center opacity-0"
            [class.opacity-100]="item.isSelected">
            <span
              class="absolute inline-flex size-2 animate-ping rounded-full bg-green-500 opacity-75 dark:bg-lime-600"></span>
            <span
              class="relative inline-flex size-2 rounded-full bg-green-500 dark:bg-lime-600"></span>
          </div>
          <button
            type="button"
            class="text-text-primary flex w-full cursor-pointer items-center px-2 py-1 text-left text-sm"
            (click)="item.action(); onCloseRecipeDropdown()"
            role="menuitem">
            {{ item.label }}
          </button>
        </div>
      }
    </div>
  </div>
</ng-template>

<ng-template #solverDropdownContent>
  <div class="min-w-48">
    <div class="max-h-96 overflow-y-auto">
      @for (item of availableSolvers(); track $index) {
        <div
          class="hover:bg-interactive flex w-full cursor-pointer flex-row items-center justify-between rounded-lg px-1 py-0.5">
          <div
            class="relative mr-0.5 ml-2 flex items-center opacity-0"
            [class.opacity-100]="item.isSelected">
            <span
              class="absolute inline-flex size-2 animate-ping rounded-full bg-green-500 opacity-75 dark:bg-lime-600"></span>
            <span
              class="relative inline-flex size-2 rounded-full bg-green-500 dark:bg-lime-600"></span>
          </div>
          <button
            type="button"
            class="text-text-primary flex w-full cursor-pointer items-center px-2 py-1 text-left text-sm"
            (click)="item.action(); onCloseSolverDropdown()"
            role="menuitem">
            {{ item.label }}
          </button>
        </div>
      }
    </div>
  </div>
</ng-template>
